import React, { useState } from "react";
import { Chip, CircularProgress, Stack, Typography } from "@mui/material";
import {
  Draw as DrawIcon,
  AccountBalance as NotaryIcon,
} from "@mui/icons-material";

const colors = {
  activeBg: "#e6f4ea",
  activeBorder: "#34a853",
  activeText: "#1e7e34",
  inactiveBg: "#ffffff",
  inactiveBorder: "#d0d5dd",
  inactiveText: "#667085",
  label: "#222222",
};

const SignatureControls = ({ signatures = {}, onUpdate, disabled = false }) => {
  const [loadingKey, setLoadingKey] = useState("");

  const owner = signatures?.owner || { sign: false, notary: false };
  const contractor = signatures?.contractor || { sign: false, notary: false };

  const handleToggle = async (person, field) => {
    if (disabled || loadingKey) return;

    const current = signatures?.[person] || { sign: false, notary: false };
    const next = { ...current };

    if (field === "sign") {
      next.sign = !current.sign;
      if (!next.sign) next.notary = false;
    }

    if (field === "notary") {
      next.notary = !current.notary;
      if (next.notary) next.sign = true;
    }

    const key = `${person}_${field}`;

    try {
      setLoadingKey(key);
      await onUpdate({ signatures: { [person]: next } });
    } finally {
      setLoadingKey("");
    }
  };

  const renderChip = (person, field, label, icon, active) => {
    const key = `${person}_${field}`;
    const isLoading = loadingKey === key;

    return (
      <Chip
        size="small"
        clickable={!disabled}
        icon={
          isLoading ? (
            <CircularProgress
              size={12}
              sx={{
                color: active ? colors.activeText : colors.inactiveText,
                ml: "6px",
              }}
            />
          ) : (
            icon
          )
        }
        label={label}
        onClick={() => handleToggle(person, field)}
        disabled={disabled || (loadingKey !== "" && !isLoading)}
        sx={{
          height: 24,
          fontSize: 12,
          fontWeight: 500,
          bgcolor: active ? colors.activeBg : colors.inactiveBg,
          color: active ? colors.activeText : colors.inactiveText,
          border: "1px solid",
          borderColor: active ? colors.activeBorder : colors.inactiveBorder,
          "& .MuiChip-icon": {
            color: active ? colors.activeText : colors.inactiveText,
          },
          "&:hover": !disabled && {
            bgcolor: active ? colors.activeBg : "#f9fafb",
            borderColor: active ? colors.activeBorder : "#98a2b3",
          },
          "&.Mui-disabled": {
            opacity: 0.5,
          },
        }}
      />
    );
  };

  const renderPerson = (label, person, value) => (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: colors.label, minWidth: 30 }}
      >
        {label}
      </Typography>
      {renderChip(
        person,
        "sign",
        "Sign",
        <DrawIcon sx={{ fontSize: 14 }} />,
        value.sign,
      )}
      {renderChip(
        person,
        "notary",
        "Notary",
        <NotaryIcon sx={{ fontSize: 14 }} />,
        value.notary,
      )}
    </Stack>
  );

  return (
    <Stack
      direction="row"
      spacing={2.5}
      alignItems="center"
      flexWrap="wrap"
      sx={{ pt: 0.75, bgcolor: "#ffffff" }}
    >
      {renderPerson("Owner", "owner", owner)}
      {renderPerson("Contractor", "contractor", contractor)}
    </Stack>
  );
};

export default SignatureControls;
